import { EntryTypeEnum, IPriceDto } from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import {
  getAvailableOptions,
  getFirstDefaultId,
  getFirstOption,
  isCombo,
  isItem,
  mapAvailableOptions,
} from 'state/unified-menu/utils';

export interface PickerAspectOptionData {
  aspectOption: ILocalizedMenuEntry;
  defaultProduct: ILocalizedMenuEntry;
  upcharge: IPriceDto;
}

export interface PickerAspectsResponse {
  pickerAspect: ILocalizedMenuEntry;
  pickerAspectOptions: PickerAspectOptionData[];
}

export function getPickerAspects(
  menu: IndexedMenu,
  posServiceMode: PosServiceMode,
  pickerAspectId: string
): PickerAspectsResponse {
  const pickerAspect = menu.get(pickerAspectId, { type: EntryTypeEnum.PICKER_ASPECT });
  const pickerAspectOptions: PickerAspectOptionData[] =
    mapAvailableOptions(
      menu,
      posServiceMode,
      pickerAspect.options?.entries,
      ({ entryId, price }) => {
        const aspectOption = menu.get(entryId, { type: EntryTypeEnum.ASPECT_OPTION });
        const aspectOptionDefaultProduct = getDefaultAspectOptionProduct(menu, aspectOption)!;
        // TODO: implement PDP V2 custom treatment to AspectOption names
        return {
          aspectOption,
          defaultProduct: aspectOptionDefaultProduct,
          upcharge: price as IPriceDto,
        };
      }
    ) || [];

  return { pickerAspect, pickerAspectOptions };
}

export function getDefaultAspectOptionProduct(
  menu: IndexedMenu,
  aspectOption: ILocalizedMenuEntry
) {
  const firstOptionId = getFirstOption(aspectOption).entryId;
  const firstProductOrAspect = menu.get(firstOptionId);

  if (isItem(firstProductOrAspect) || isCombo(firstProductOrAspect)) {
    return firstProductOrAspect;
  }

  // If it's not a Product (Combo/Item) it's another AspectOption.
  // We iterate recursively until we find a Product.
  const defaultChildId = getFirstDefaultId(firstProductOrAspect);
  const nextAspectOption = menu.get(defaultChildId, { type: EntryTypeEnum.ASPECT_OPTION });
  return getDefaultAspectOptionProduct(menu, nextAspectOption);
}

export interface MinMaxCalories {
  min: number;
  max: number;
}

export function getAspectOptionMinMaxCalories(
  menu: IndexedMenu,
  posServiceMode: PosServiceMode,
  aspectOptionId: string
): MinMaxCalories {
  const acc: MinMaxCalories = { min: Infinity, max: -Infinity };
  const aspectOption = menu.get(aspectOptionId, { type: EntryTypeEnum.ASPECT_OPTION });
  assignAspectOptionMinMaxCalories(menu, posServiceMode, aspectOption, acc);
  if (acc.min === Infinity && acc.max === -Infinity) {
    return { min: 0, max: 0 };
  } else if (acc.min === Infinity) {
    return { min: acc.max, max: acc.max };
  } else if (acc.max === -Infinity) {
    return { min: acc.min, max: acc.min };
  }
  return acc;
}

function assignAspectOptionMinMaxCalories(
  menu: IndexedMenu,
  posServiceMode: PosServiceMode,
  aspectOption: ILocalizedMenuEntry,
  acc: MinMaxCalories
): void {
  const firstOptionId = getFirstOption(aspectOption).entryId;
  const firstProductOrAspect = menu.get(firstOptionId);

  if (isItem(firstProductOrAspect) || isCombo(firstProductOrAspect)) {
    const calories = firstProductOrAspect.nutrition?.calories;
    if (calories) {
      acc.min = Math.min(acc.min, calories.min || calories.def || acc.min);
      acc.max = Math.max(acc.max, calories.max || calories.def || acc.max);
    }
    return;
  }

  const options = getAvailableOptions(
    firstProductOrAspect.options?.entries,
    posServiceMode,
    undefined
  );
  for (const option of options) {
    const optionEntry = menu.get(option.entryId);
    assignAspectOptionMinMaxCalories(menu, posServiceMode, optionEntry, acc);
  }
}
